/**
 * AutoFillProgressDialog
 *
 * Shows a phase-by-phase animated progress dialog while the Auto-Fill
 * operation runs and then replays the result slot-by-slot after completion.
 *
 * Strategy (Option A — simulated replay):
 *   The autoFillTimetable API call completes instantly and returns the full
 *   result. We open the dialog immediately when the button is clicked, show
 *   a real loading state while the API runs, then animate through the result
 *   data at a human-readable pace so the user can see what was assigned.
 *
 * Phases:
 *   1. loading   — API call in flight (indeterminate progress bar)
 *   2. scanning  — "Found N empty slots" reveal
 *   3. assigning — slot cards flip from pending → assigned / unassigned one by one
 *   4. complete  — summary dashboard
 *   5. empty     — nothing to fill (all slots already filled)
 *   6. error     — API threw an error
 *
 * Timing:
 *   SCAN_DISPLAY_MS  — how long the "scanning" phase is shown before assigning starts
 *   SLOT_REVEAL_MS   — delay between each slot card being revealed
 *   MIN_PHASE_MS     — minimum time each phase is visible
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  LinearProgress,
  Chip,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonIcon from '@mui/icons-material/Person';
import type { AutoFillResponse } from '@timetable/types';

// ─── Constants ────────────────────────────────────────────────────────────────

/** How long the "scanning" phase is shown before slot reveal starts */
const SCAN_DISPLAY_MS = 700;
/** Delay between each slot card being revealed during the assigning phase */
const SLOT_REVEAL_MS = 120;
/** Minimum time the complete phase is shown before the button becomes active */
const MIN_COMPLETE_MS = 400;

// ─── Types ────────────────────────────────────────────────────────────────────

export type AutoFillPhase = 'loading' | 'scanning' | 'assigning' | 'complete' | 'empty' | 'error';

/** Resolved slot card — IDs replaced with display names */
export interface AutoFillSlotCard {
  key: string; // sectionId|periodId|day
  sectionId: string;
  periodId: string;
  day: string;
  periodNumber: number;
  timeStart: string;
  timeEnd: string;
  subjectName: string;
  sectionLabel: string;
  teacherName?: string;
  reason?: string;
  status: 'pending' | 'assigned' | 'unassigned';
}

// ─── Lookup maps passed in from the page ─────────────────────────────────────

export interface AutoFillLookups {
  /** periodId → { period_number, time_start, time_end } */
  periodsById: Map<string, { period_number: number; time_start: string; time_end: string }>;
  /** subjectId → subject_name */
  subjectsById: Map<string, string>;
  /** sectionId → section label (e.g. "Class IA") */
  sectionsById: Map<string, string>;
  /** teacherId → teacher name */
  teachersById: Map<string, string>;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AutoFillProgressDialogProps {
  open: boolean;
  /** Whether the API call is still in flight */
  isLoading: boolean;
  /** Result from autoFillTimetable — null while loading or on error */
  result: AutoFillResponse | null;
  /** Error message if the API call failed */
  errorMessage?: string | null;
  /** Lookup maps to resolve IDs → display names */
  lookups: AutoFillLookups;
  /** Called when user closes the dialog */
  onClose: () => void;
}

// ─── Phase step bar ───────────────────────────────────────────────────────────

const PHASE_STEPS = [
  { phase: 'scanning' as AutoFillPhase,  label: 'Slot Scan',   icon: <SearchIcon sx={{ fontSize: 16 }} /> },
  { phase: 'assigning' as AutoFillPhase, label: 'Assigning',   icon: <AutoFixHighIcon sx={{ fontSize: 16 }} /> },
  { phase: 'complete' as AutoFillPhase,  label: 'Done',        icon: <EmojiEventsIcon sx={{ fontSize: 16 }} /> },
];

const PHASE_ORDER: AutoFillPhase[] = ['scanning', 'assigning', 'complete'];

function phaseIndex(phase: AutoFillPhase): number {
  return PHASE_ORDER.indexOf(phase);
}

function PhaseStepBar({ currentPhase }: { currentPhase: AutoFillPhase }) {
  const isSpecial = currentPhase === 'loading' || currentPhase === 'empty' || currentPhase === 'error';
  const activeIdx = isSpecial ? -1 : phaseIndex(currentPhase);

  return (
    <Box sx={{ display: 'flex', gap: 0, mb: 2.5 }}>
      {PHASE_STEPS.map((step, idx) => {
        const isDone   = !isSpecial && activeIdx > idx;
        const isActive = !isSpecial && activeIdx === idx;

        return (
          <Box
            key={step.phase}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              position: 'relative',
              '&:not(:last-child)::after': {
                content: '""',
                position: 'absolute',
                top: 12,
                left: '50%',
                width: '100%',
                height: 2,
                bgcolor: isDone ? '#0F766E' : 'divider',
                transition: 'background-color 0.3s',
              },
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isDone ? '#0F766E' : isActive ? 'primary.main' : 'action.hover',
                color: isDone || isActive ? '#fff' : 'text.disabled',
                transition: 'all 0.3s',
                zIndex: 1,
              }}
            >
              {isDone ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : step.icon}
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: 10,
                fontWeight: isActive ? 700 : 400,
                color: isDone ? '#0F766E' : isActive ? 'primary.main' : 'text.disabled',
                textAlign: 'center',
                lineHeight: 1.2,
              }}
            >
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Slot card ────────────────────────────────────────────────────────────────

function SlotCard({ card }: { card: AutoFillSlotCard }) {
  const isAssigned   = card.status === 'assigned';
  const isUnassigned = card.status === 'unassigned';
  const isPending    = card.status === 'pending';

  const borderColor = isAssigned ? '#0F766E' : isUnassigned ? '#92400E' : 'divider';
  const bgColor     = isAssigned ? '#F0FDFA'  : isUnassigned ? '#FFFBEB'  : 'background.paper';

  const dayLabel = card.day.charAt(0) + card.day.slice(1).toLowerCase();

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor,
        borderRadius: 1.5,
        p: 1.5,
        bgcolor: bgColor,
        minWidth: 190,
        flexShrink: 0,
        transition: 'all 0.25s ease',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="caption" fontWeight="bold" color="text.secondary">
          P{card.periodNumber} · {card.timeStart.slice(0, 5)}–{card.timeEnd.slice(0, 5)}
        </Typography>
        {isAssigned   && <CheckCircleIcon sx={{ fontSize: 14, color: '#0F766E' }} />}
        {isUnassigned && <WarningAmberIcon sx={{ fontSize: 14, color: '#92400E' }} />}
      </Box>

      {/* Subject */}
      <Typography variant="body2" fontWeight="medium" noWrap sx={{ mb: 0.25 }}>
        {card.subjectName}
      </Typography>

      {/* Section + day */}
      <Typography variant="caption" color="text.secondary" noWrap>
        {card.sectionLabel} · {dayLabel}
      </Typography>

      {/* Result */}
      {isPending && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: 'block' }}>
          Waiting…
        </Typography>
      )}
      {isAssigned && card.teacherName && (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="caption" fontWeight="bold" color="#0F766E" noWrap>
            {card.teacherName}
          </Typography>
        </Box>
      )}
      {isUnassigned && (
        <Typography variant="caption" color="warning.dark" sx={{ mt: 0.75, display: 'block' }}>
          No teacher available
        </Typography>
      )}
    </Box>
  );
}

// ─── Summary dashboard ────────────────────────────────────────────────────────

function SummaryDashboard({
  totalSlots,
  assigned,
  unassigned,
  uniqueTeachers,
  elapsedMs,
}: {
  totalSlots: number;
  assigned: number;
  unassigned: number;
  uniqueTeachers: number;
  elapsedMs: number;
}) {
  const allCovered = unassigned === 0 && assigned > 0;

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        {allCovered ? (
          <>
            <CheckCircleIcon sx={{ fontSize: 40, color: '#0F766E', mb: 0.5 }} />
            <Typography variant="h6" fontWeight="bold" color="#0F766E">
              Timetable filled!
            </Typography>
          </>
        ) : unassigned > 0 ? (
          <>
            <WarningAmberIcon sx={{ fontSize: 40, color: '#92400E', mb: 0.5 }} />
            <Typography variant="h6" fontWeight="bold" color="#92400E">
              Partial fill — some slots need attention
            </Typography>
          </>
        ) : (
          <>
            <CheckCircleIcon sx={{ fontSize: 40, color: '#0F766E', mb: 0.5 }} />
            <Typography variant="h6" fontWeight="bold" color="#0F766E">
              Fill complete
            </Typography>
          </>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
        <StatCard
          value={`${assigned}/${totalSlots}`}
          label="Slots filled"
          color="#0F766E"
          bg="#F0FDFA"
        />
        <StatCard
          value={String(unassigned)}
          label="Need attention"
          color={unassigned > 0 ? '#92400E' : 'text.secondary'}
          bg={unassigned > 0 ? '#FFFBEB' : 'action.hover'}
        />
        <StatCard
          value={String(uniqueTeachers)}
          label="Teachers assigned"
          color="text.primary"
          bg="action.hover"
        />
        <StatCard
          value={`${(elapsedMs / 1000).toFixed(1)}s`}
          label="Completed in"
          color="text.secondary"
          bg="action.hover"
        />
      </Box>

      {unassigned > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}>
          {unassigned} slot{unassigned !== 1 ? 's' : ''} could not be automatically filled.
          Click the empty cell in the grid to assign manually.
        </Alert>
      )}

      {assigned > 0 && (
        <Alert severity="success" sx={{ fontSize: 12 }}>
          {assigned} slot{assigned !== 1 ? 's' : ''} filled successfully. Review the timetable grid to verify.
        </Alert>
      )}
    </Box>
  );
}

function StatCard({ value, label, color, bg }: { value: string; label: string; color: string; bg: string }) {
  return (
    <Box sx={{ bgcolor: bg, borderRadius: 1.5, p: 1.5, textAlign: 'center' }}>
      <Typography variant="h5" fontWeight="bold" color={color}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

export function AutoFillProgressDialog({
  open,
  isLoading,
  result,
  errorMessage,
  lookups,
  onClose,
}: AutoFillProgressDialogProps) {
  const [phase, setPhase] = useState<AutoFillPhase>('loading');
  const [revealedCount, setRevealedCount] = useState(0);
  const [allCards, setAllCards] = useState<AutoFillSlotCard[]>([]);
  const [startTime, setStartTime] = useState<number>(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  const revealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scanTimerRef   = useRef<ReturnType<typeof setTimeout>  | null>(null);

  // ── Cleanup ──────────────────────────────────────────────────────────────
  const clearTimers = useCallback(() => {
    if (revealTimerRef.current) { clearInterval(revealTimerRef.current); revealTimerRef.current = null; }
    if (scanTimerRef.current)   { clearTimeout(scanTimerRef.current);    scanTimerRef.current   = null; }
  }, []);

  // ── Reset when dialog opens ───────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      clearTimers();
      setPhase('loading');
      setRevealedCount(0);
      setAllCards([]);
      setStartTime(Date.now());
      setElapsedMs(0);
    } else {
      clearTimers();
    }
  }, [open, clearTimers]);

  // ── React to result arriving ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    if (errorMessage) {
      setPhase('error');
      return;
    }

    if (!result) return; // still loading

    const elapsed = Date.now() - startTime;
    setElapsedMs(elapsed);

    const totalSlots = result.assignments.length + result.unassigned.length;

    if (totalSlots === 0) {
      setPhase('empty');
      return;
    }

    // Build resolved slot cards — assigned first, then unassigned
    const cards: AutoFillSlotCard[] = [
      ...result.assignments.map((a): AutoFillSlotCard => {
        const period  = lookups.periodsById.get(a.periodId);
        const subject = lookups.subjectsById.get(a.subjectId) ?? 'Unknown Subject';
        const section = lookups.sectionsById.get(a.sectionId) ?? a.sectionId;
        const teacher = lookups.teachersById.get(a.teacherId) ?? a.teacherId;
        return {
          key: `${a.sectionId}|${a.periodId}|${a.day}`,
          sectionId: a.sectionId,
          periodId: a.periodId,
          day: a.day,
          periodNumber: period?.period_number ?? 0,
          timeStart: period?.time_start ?? '',
          timeEnd: period?.time_end ?? '',
          subjectName: subject,
          sectionLabel: section,
          teacherName: teacher,
          reason: a.reason,
          status: 'assigned',
        };
      }),
      ...result.unassigned.map((u): AutoFillSlotCard => {
        const period  = lookups.periodsById.get(u.periodId);
        const section = lookups.sectionsById.get(u.sectionId) ?? u.sectionId;
        return {
          key: `${u.sectionId}|${u.periodId}|${u.day}`,
          sectionId: u.sectionId,
          periodId: u.periodId,
          day: u.day,
          periodNumber: period?.period_number ?? 0,
          timeStart: period?.time_start ?? '',
          timeEnd: period?.time_end ?? '',
          subjectName: '—',
          sectionLabel: section,
          status: 'unassigned',
          reason: u.reason,
        };
      }),
    ];

    // Sort by period number then day for a natural reading order
    cards.sort((a, b) => a.periodNumber - b.periodNumber || a.day.localeCompare(b.day));

    setAllCards(cards);
    setRevealedCount(0);

    // Phase 2: scanning
    setPhase('scanning');

    // After SCAN_DISPLAY_MS, start revealing cards one by one
    scanTimerRef.current = setTimeout(() => {
      setPhase('assigning');

      let count = 0;
      revealTimerRef.current = setInterval(() => {
        count += 1;
        setRevealedCount(count);
        if (count >= cards.length) {
          clearInterval(revealTimerRef.current!);
          revealTimerRef.current = null;
          setTimeout(() => setPhase('complete'), MIN_COMPLETE_MS);
        }
      }, SLOT_REVEAL_MS);
    }, SCAN_DISPLAY_MS);

    return clearTimers;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, errorMessage, open]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const isComplete = phase === 'complete';
  const isError    = phase === 'error';
  const isEmpty    = phase === 'empty';
  const isDone     = isComplete || isError || isEmpty;

  const assignedCount   = result?.assignments.length ?? 0;
  const unassignedCount = result?.unassigned.length ?? 0;
  const totalSlots      = assignedCount + unassignedCount;
  const uniqueTeachers  = result
    ? new Set(result.assignments.map((a) => a.teacherId)).size
    : 0;

  // Progress bar value
  const progressValue = (() => {
    if (phase === 'loading')   return 0;
    if (phase === 'scanning')  return 20;
    if (phase === 'assigning' && totalSlots > 0) {
      return 20 + (revealedCount / totalSlots) * 70;
    }
    if (phase === 'complete')  return 100;
    if (phase === 'empty' || phase === 'error') return 100;
    return 0;
  })();

  // Cards to show: only revealed ones, rest shown as pending
  const visibleCards: AutoFillSlotCard[] = allCards.map((card, idx) =>
    idx < revealedCount ? card : { ...card, status: 'pending' as const }
  );

  return (
    <Dialog
      open={open}
      onClose={isDone ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      {/* ── Title + progress bar ── */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isLoading || phase === 'loading' ? (
            <CircularProgress size={18} color="primary" />
          ) : isComplete ? (
            <CheckCircleIcon sx={{ color: '#0F766E', fontSize: 20 }} />
          ) : isError ? (
            <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 20 }} />
          ) : isEmpty ? (
            <CheckCircleIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          ) : (
            <AutoFixHighIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          )}
          <Typography variant="h6" fontWeight="bold">
            {isLoading || phase === 'loading'
              ? 'Auto-Filling…'
              : isComplete
              ? 'Fill Complete'
              : isError
              ? 'Auto-Fill Failed'
              : isEmpty
              ? 'Nothing to Fill'
              : 'Auto-Filling…'}
          </Typography>
        </Box>

        <LinearProgress
          variant={isLoading || phase === 'loading' ? 'indeterminate' : 'determinate'}
          value={progressValue}
          sx={{
            mt: 1.5,
            height: 4,
            borderRadius: 2,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              bgcolor: isError ? 'error.main' : '#0F766E',
              transition: 'transform 0.4s ease',
            },
          }}
        />
      </DialogTitle>

      {/* ── Content ── */}
      <DialogContent sx={{ pt: 1 }}>

        {/* Loading state */}
        {(isLoading || phase === 'loading') && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Analysing timetable and finding best teachers…
            </Typography>
          </Box>
        )}

        {/* Scanning / assigning / complete phases */}
        {!isLoading && phase !== 'loading' && !isError && !isEmpty && (
          <Box>
            <PhaseStepBar currentPhase={phase} />

            {/* Scanning phase info box */}
            {phase === 'scanning' && (
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5, mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  📅 {totalSlots} empty slot{totalSlots !== 1 ? 's' : ''} found
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Preparing teacher assignments…
                </Typography>
              </Box>
            )}

            {/* Assigning phase: summary chips */}
            {(phase === 'assigning' || phase === 'complete') && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<PersonIcon sx={{ fontSize: 14 }} />}
                  label={`${assignedCount} assigned`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 22, fontSize: 11 }}
                />
                {unassignedCount > 0 && (
                  <Chip
                    label={`${unassignedCount} unassigned`}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11 }}
                  />
                )}
                <Chip
                  label={`${totalSlots} total slots`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: 11 }}
                />
              </Box>
            )}

            {/* Slot cards — scrollable horizontal row */}
            {(phase === 'assigning' || phase === 'complete') && visibleCards.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                  Slot assignments
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
                  {visibleCards.map((card) => (
                    <SlotCard key={card.key} card={card} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Complete summary */}
            {isComplete && (
              <SummaryDashboard
                totalSlots={totalSlots}
                assigned={assignedCount}
                unassigned={unassignedCount}
                uniqueTeachers={uniqueTeachers}
                elapsedMs={elapsedMs}
              />
            )}
          </Box>
        )}

        {/* Empty state */}
        {isEmpty && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" color="text.secondary" fontWeight="medium">
              All slots are already filled
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
              There are no empty periods to assign for this class.
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {isError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorMessage || 'Auto-fill failed. Please try again.'}
          </Alert>
        )}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          variant={isDone ? 'contained' : 'outlined'}
          disabled={!isDone && !isLoading}
          onClick={onClose}
          color={isError ? 'error' : 'primary'}
        >
          {isComplete ? 'Done' : isEmpty ? 'Close' : isError ? 'Dismiss' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
